/**
 * Overlay badge with up to 3 avatars of friends who RSVP'd going/maybe,
 * plus a "+N more" tail. Reads `event.friend_attendees` + `.friend_attendees_total`
 * which the server joins into every authenticated response.
 *
 * Renders nothing when there are no friends attending — the caller does not
 * need to conditionally hide it; this component is already null-safe.
 */
import type { PlaceEvent } from '@/types/api';

export interface FriendAttendeesBadgeProps {
  event: PlaceEvent;
  /** Default `sm`. `md` is used on the event detail page where there is more room. */
  size?: 'sm' | 'md';
}

export function FriendAttendeesBadge({ event, size = 'sm' }: FriendAttendeesBadgeProps) {
  const list = event.friend_attendees ?? [];
  if (list.length === 0) return null;

  const total = event.friend_attendees_total ?? list.length;
  const extra = Math.max(0, total - list.length);
  const dim = size === 'md' ? 'h-7 w-7 text-xs' : 'h-6 w-6 text-[10px]';

  return (
    <div className="flex items-center gap-1" aria-label={`${total} of your friends attending`}>
      <div className="flex -space-x-2">
        {list.map((f) => (
          f.image ? (
            <img
              key={f.id}
              src={f.image}
              alt=""
              title={f.firstname || f.username}
              className={`${dim} rounded-full border-2 border-white object-cover`}
            />
          ) : (
            <span
              key={f.id}
              title={f.firstname || f.username}
              className={`${dim} flex items-center justify-center rounded-full border-2 border-white bg-brand-100 font-semibold text-brand-700`}
              aria-hidden
            >
              {(f.firstname || f.username || '?').slice(0, 1).toUpperCase()}
            </span>
          )
        ))}
      </div>
      <span className="text-xs text-muted-foreground">
        {extra > 0 ? `+${extra} more` : ''}
      </span>
    </div>
  );
}
